import { Ref, ref, isRef, computed } from '@vue/composition-api';
import { isEqual } from 'lodash-es';

type ReturnType<T> = {
  changes: Ref<Partial<T>>;
  isDirty: Ref<boolean>;
  object: Ref<T>;
};
export function useObjectWithChangeDetection<T extends object> (objectRef: Ref<T>): ReturnType<T>;
export function useObjectWithChangeDetection<T extends object> (objLiteral: T): ReturnType<T>;
export function useObjectWithChangeDetection<T extends object> (obj: T) {
  const changes: Ref<Partial<T>> = ref({});
  const object: Ref<T> = computed(() => {
    const unwrappedTarget = isRef<T>(obj) ? obj.value : obj;
    return unwrappedTarget && new Proxy(unwrappedTarget, {
      get (target, key) {
        // prevent Vue to define reactive property on the object to be tracked
        // this guarantee reactivity for fields that are not present in the original object to be tracked in changes
        if (key === '__ob__') return undefined;
        return key in changes.value ? changes.value[key] : target[key];
      },
      set (target, key, value) {
        const dirty = !isEqual(target[key], value);
        if (dirty) {
          changes.value = { ...changes.value, [key]: value };
        } else {
          const clone = { ...changes.value };
          delete clone[key];
          changes.value = clone;
        }
        return true;
      },
    });
  });
  return {
    changes,
    isDirty: computed(() => Object.entries(changes.value).length > 0),
    object,
  };
}
